import { includes } from 'lodash';
import { Settings } from './../../core/settings';
import { SponsorActivityReportFilterItemsStorage } from './sponsor-activity-report-filter-items-storage';
import { SponsorActivityReportDocumentsStorage, RESTRICTED_SPONSOR_REPORTS_CATEGORIES } from './sponsor-activity-report-documents-storage';
import { Component, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { Category, CategoriesStorage, CategoryNaturalKey } from './../../documents';
import { Observable, Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { map, flatMap, publishReplay, refCount, distinctUntilChanged, withLatestFrom, filter } from 'rxjs/operators';
import { ColDef, GridOptions } from 'ag-grid-community';
import { UrlBuilder } from './../../core/index';
import { FilterFieldValue } from '../../covance-filter';
import { FILTER_ITEM_LOADER } from '../../documents/filter-items-loader';
import { DOCUMENTS_STORAGE } from '../../documents/documents-storage';
import { SponsorActivityReportsDocumentsColumns } from './sponsor-activity-reports-documents-columns';
import { SponsorStudy } from '../../sponsor-dashboard/sponsor-study';
import { SponsorActivityReportsFilterResolver } from './sponsor-activity-reports-filter-resolver';
import { SponsorRouteData } from '../sponsor-route-data';
import { routeParams, findProtocolId, findAccessionNo } from '../../shared/route-helpers';
import { FILTER_RESOLVER } from '../../documents/filter-resolver';

@Component({
    selector: 'sponsor-activity-report-documents',
    template: require('./sponsor-activity-report-documents.component.html'),
    styles: [require('./sponsor-activity-report-documents.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: FILTER_ITEM_LOADER, useClass: SponsorActivityReportFilterItemsStorage },
        { provide: DOCUMENTS_STORAGE, useClass: SponsorActivityReportDocumentsStorage },
        { provide: FILTER_RESOLVER, useExisting: SponsorActivityReportsFilterResolver },
    ]
})
export class SponsorActivityReportDocumentsComponent implements OnInit, OnDestroy {
    public categories: Observable<Category[]>;
    public readonly gridOptions: GridOptions = {
        floatingFilter: false
    };

    public categoryIdChangeFlow$ = new BehaviorSubject<CategoryNaturalKey>(null);
    public selectedCategory: Observable<Category>;
    public selectedCategoryId: Observable<CategoryNaturalKey>;
    public selectedStudy: Observable<SponsorStudy>;
    public columns: Observable<ColDef[]>;
    public filterFieldDefs: Observable<FilterFieldValue[]>;
    public adminFilterGroup: Observable<string>;
    public warningThreshhold: Observable<number>;
    public protocolId: number;
    public quickFilterText: string;
    private subscription = new Subscription();
    
    constructor(
        private readonly route: ActivatedRoute,
        private readonly routeData: SponsorRouteData,
        private readonly categoriesStorage: CategoriesStorage,
        private readonly documentsColumns: SponsorActivityReportsDocumentsColumns,
        private readonly urlBuilder: UrlBuilder,
        private readonly settings: Settings
    ) {        
        this.protocolId = findProtocolId(this.route.snapshot);
        this.quickFilterText = findAccessionNo(this.route.snapshot);
    }

    public ngOnInit() {
        this.categories = combineLatest(
            this.routeData.studyPermissions,
            this.categoriesStorage.activityReportCategories
        ).pipe(
            map(([permissions, categories]) =>
                categories.filter(category => permissions && permissions.canViewActivityReport(category.categoryId))
            ),
            publishReplay(1),
            refCount()
        );

        this.selectedStudy = this.routeData.selectedStudy;

        this.selectedCategoryId = this.route.params.pipe(
            map(x => x[routeParams.categoryId] as CategoryNaturalKey | undefined),
            distinctUntilChanged(),
            publishReplay(1),
            refCount()
        );

        this.warningThreshhold = this.selectedCategoryId.pipe(
            map(c => includes(RESTRICTED_SPONSOR_REPORTS_CATEGORIES, c) ? this.settings.sqlRequestLimitForActivityReports : 0)
        );

        this.selectedCategory = this.selectedCategoryId.pipe(
            flatMap(categoryId => this.categoriesStorage.findCategoryById(categoryId as CategoryNaturalKey)),
            publishReplay(1),
            refCount(),
        );

        this.columns = this.documentsColumns.getColumnDefinitions(this.selectedCategory);

        this.subscription.add(
            this.categoryIdChangeFlow$.pipe(
                filter(c => !!c),
                withLatestFrom(this.selectedStudy)
            ).subscribe(([categoryId, study]) => {
                this.urlBuilder.esa().sponsor().study(study).activityReports().documents(categoryId).navigate();
            })
        );
    }

    public ngOnDestroy() {
        this.subscription.unsubscribe();
    }
} 